import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '@flexibilitypay/ui';

import { ROUTES } from '../routes/routes-config';
import { useBalanceQuery } from '../hooks/use-balance-query';
import { BalanceCard, BalanceSkeleton } from '../features/balance';
import { ComplianceActionsPanel } from '../features/compliance';
import { RoleGate } from '../shared/components/role-gate';

function useBalanceErrorMessage(
  t: (key: string) => string,
  error: Error | null | undefined,
): string | null {
  if (!error) return null;
  const msg = error.message;
  if (msg === 'HTTP_404') return t('balance:errors.notFound');
  if (msg === 'HTTP_503' || msg === 'HTTP_502' || msg === 'HTTP_500') {
    return t('balance:errors.unavailable');
  }
  return t('balance:errors.generic');
}

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T058
 * @taskId T061
 * @taskId T060
 * @see functional/Specs/specs_cycle-2.md HU-27-BE
 * @see functional/Specs/specs_cycle-2.md HU-06-FE
 * @see functional/Specs/specs_cycle-2.md HU-13-FE
 * @see functional/Specs/specs_cycle-2.md HU-11-FE
 */
export function BalancePage() {
  const { idCuenta } = useParams<{ idCuenta: string }>();
  const { t } = useTranslation(['balance']);
  const { data, isLoading, isError, error } = useBalanceQuery(idCuenta);
  const errorMessage = useBalanceErrorMessage(t, error as Error | null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('balance:page.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('balance:page.subtitle')}</p>
      </div>

      {isLoading ? (
        <BalanceSkeleton />
      ) : (
        <BalanceCard data={data} isError={isError} errorMessage={errorMessage} />
      )}

      {idCuenta && (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <Link to={ROUTES.MOVEMENTS_DETAIL.replace(':idCuenta', idCuenta)}>
              {t('balance:page.viewMovements')}
              <ArrowRight className="ml-2 size-4" aria-hidden />
            </Link>
          </Button>
        </div>
      )}

      {idCuenta && (
        <RoleGate allow="PSP_ADMIN">
          <ComplianceActionsPanel accountId={idCuenta} />
        </RoleGate>
      )}
    </div>
  );
}
